import React, { memo, useCallback, useMemo, useEffect } from 'react'

import {
  Icon,
  Button,
  AIGenerated,
  Check,
  Tooltip,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, string } from 'prop-types'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { iconSize } from 'Pods/ThreeDStudio/constants'
import useOpenedModelReadOnly from 'Pods/ThreeDStudio/Hooks/useOpenedModelReadOnly'
import { buildGeneratePayload } from 'Pods/ThreeDStudio/Services/buildGeneratePayload'
import { buildOpenedModelFromState }
  from 'Pods/ThreeDStudio/Services/buildOpenedModelFromState/buildOpenedModelFromState'
import { getEnhancedSettingsKey } from 'Pods/ThreeDStudio/Shared/Utils/getEnhancedSettingsKey'
import { useThreeDStudioStore, threeDStudioStore } from 'Pods/ThreeDStudio/Store'
import {
  setIsGenerating,
  setIsEnhancing,
  setEnhancingSessionId,
  setRunId,
  setSessionId,
  setHasGenerated,
  setLastGenerationSettings,
  setAvailableCredits,
  setHasFailed,
  setTriggeredBy,
} from 'Pods/ThreeDStudio/Store/actions/generation'
import { openSignInModal, openUsageModal } from 'Pods/ThreeDStudio/Store/actions/ui'
import { GENERATION_TRIGGER_KEY, STUDIO_ACCESS_TIER } from 'Pods/ThreeDStudio/Store/constants'

import useAreRequiredParamsFulfilled from './Hooks/useAreRequiredParamsFulfilled'

const MODAL_TIERS = new Set([
  STUDIO_ACCESS_TIER.ELIGIBLE_FOR_TRIAL,
  STUDIO_ACCESS_TIER.MUST_JOIN_WAITLIST,
  STUDIO_ACCESS_TIER.NOT_SUBSCRIBED,
  STUDIO_ACCESS_TIER.WAITLISTED,
])

const GENERATION_IN_PROGRESS_TOOLTIP =
  'Another generation is currently in progress. This action will be available once it has completed.'

function GenerateButton({
  generateLabel,
  generatedLabel,
  generatingLabel,
  isEnhancementMode = false,
  isDisabled = false,
  tooltipMessage = undefined,
  withIcon = true,
}) {
  const pageConfig = usePageConfig()
  const { isCurrentUser: isLoggedIn } = pageConfig || {}
  const isReadOnly = useOpenedModelReadOnly()

  const areRequiredParamsFulfilled = useAreRequiredParamsFulfilled(isEnhancementMode)

  const { postData, isLoading } = useApiPost()
  const navigate = useNavigate()
  const location = useLocation()
  const isGenerating = useThreeDStudioStore(state => state.generation.isGenerating)
  const triggeredBy = useThreeDStudioStore(state => state.generation.triggeredBy)
  const activeViewerSidebarTab = useThreeDStudioStore(state => state.ui.activeViewerSidebarTab)
  const hasGenerated = useThreeDStudioStore(state => state.generation.hasGenerated)
  const studioAccessTier = useThreeDStudioStore(state => state.subscription.studioAccessTier)
  const isModalTier = MODAL_TIERS.has(studioAccessTier)
  const isQuotaHydrating = isLoggedIn === true && studioAccessTier === null

  // Enhancement buttons are keyed by their tab; the main/initial generate button
  // uses the GENERATION_TRIGGER_KEY sentinel. Only the button whose key matches the
  // run's recorded trigger shows the in-progress label; the rest are disabled and,
  // on hover, explain why.
  const triggerKey = isEnhancementMode ? activeViewerSidebarTab : GENERATION_TRIGGER_KEY
  const isThisButtonGenerating = isGenerating && triggeredBy === triggerKey
  const isBlockedByActiveGeneration = isGenerating && !isThisButtonGenerating

  useEffect(() => {
    if (hasGenerated) {
      setTimeout(() => {
        setHasGenerated(false)
      }, 3000)
    }
  }, [hasGenerated])

  const handleClick = useCallback(() => {
    if (!isLoggedIn) {
      openSignInModal('signup')
      return
    }

    const state = threeDStudioStore.getState()
    const { subscription, generation } = state
    const { studioAccessTier: currentTier, generationCost } = subscription

    if (currentTier === null || currentTier === undefined) {
      return
    }

    if (MODAL_TIERS.has(currentTier)) {
      openUsageModal()
      return
    }

    if (typeof generationCost !== 'number' || generationCost <= 0) {
      return
    }

    // The wallet is the source of truth for spendability: trial and subscription
    // grants live in wallet buckets, so the credits check below already covers
    // exhausted quotas. Re-gating on monthly/extra counters here falsely blocked
    // users whose trial was drained but who held credits in other buckets.
    const hasEnoughCredits = (generation.availableCredits ?? 0) >= generationCost

    if (!hasEnoughCredits) {
      openUsageModal()
      return
    }

    const currentSidebarTab = state.ui.activeViewerSidebarTab
    const settingsKey = isEnhancementMode
      ? getEnhancedSettingsKey(currentSidebarTab, state.schema.enhancedAction)
      : 'modelSettings'
    const schema = isEnhancementMode
      ? state.schema.enhancedModelSchema
      : state.schema.generationModelSchema
    const additionalPayloadData = (() => {
      if (isEnhancementMode) {
        // Enhancing someone else's model starts a fresh session under the current
        // user; sending the owner's generator_session_id would target their session.
        return state.generation.generatorSessionId && !isReadOnly
          ? { generator_session_id: state.generation.generatorSessionId }
          : {}
      }

      const { isPrivate, selectedUseCase } = state.modelSettings
      return {
        private: isPrivate,
        ...(!isEnhancementMode && selectedUseCase ? { use_case: selectedUseCase } : {}),
      }
    })()

    const payload = buildGeneratePayload(settingsKey, schema, additionalPayloadData)
    // Only an enhancement of an owned model that already has a generator session
    // adds a variation in place and drives the "Processing..." overlay on that
    // session's library card. Enhancing a model the user does not own, or generating
    // from a freshly uploaded model (no session yet), starts a fresh session - there
    // is no existing card to overlay, so it shows the loading card like an initial
    // generation instead.
    const enhancingSessionId = isEnhancementMode ? state.ui.openedModel?.generatorSessionId : undefined
    const isOwnedEnhancement = isEnhancementMode && !isReadOnly && enhancingSessionId != null
    setIsGenerating(true)
    setIsEnhancing(isOwnedEnhancement)
    setTriggeredBy(isEnhancementMode ? currentSidebarTab : GENERATION_TRIGGER_KEY)

    if (isOwnedEnhancement) {
      setEnhancingSessionId(enhancingSessionId)
    }

    postData({
      path: '/api/internal/generator/runs',
      data: payload,
      onSuccess: (response) => {
        const responseData = response.data.data.attributes
        const { id, generatorSessionId, remainingCredits } = responseData
        setRunId(id)
        setSessionId(generatorSessionId)
        const onSuccessState = threeDStudioStore.getState()
        setAvailableCredits(remainingCredits.total_credits)

        if (!isEnhancementMode) {
          if (location?.pathname !== '/ai') {
            navigate('/ai', { replace: true })
          }
          const lastGenerationSettings = buildOpenedModelFromState(onSuccessState, id)
          setLastGenerationSettings(lastGenerationSettings)
        }
      },
      onError: (error) => {
        // todo add error id when api returns it
        setIsGenerating(false)
        setHasFailed(true)
        const errorMessage = error?.response?.data?.errors?.[0] || 'Generation failed. Please try again.'
        if (typeof window !== 'undefined' && window.showNotification) {
          window.showNotification(errorMessage, { type: 'error', autohide: true })
        }
      },
    })
  }, [navigate, location, isEnhancementMode, isLoggedIn, isReadOnly, postData])

  const label = useMemo(() => {
    if (isThisButtonGenerating) {
      return generatingLabel
    }
    if (hasGenerated) {
      return generatedLabel
    }

    return generateLabel
  }, [isThisButtonGenerating, hasGenerated, generateLabel, generatedLabel, generatingLabel])

  const buttonIcon = useMemo(
    () => <Icon svgComponent={hasGenerated ? Check : AIGenerated} color="light" size={iconSize} notSpaced />,
    [hasGenerated],
  )

  const button = (
    <Button
      {...(withIcon && {
        icon: buttonIcon,
        variant: 'with-icon',
      })}
      type="ghost"
      size="small"
      customClass={
        classnames(
          'generate-area__button cgt-body-base-semibold',
          hasGenerated && 'generate-area__button--generated',
          !withIcon && 'generate-area__button--no-icon'
        )
      }
      onClick={handleClick}
      disabled={
        isDisabled
        || isQuotaHydrating
        || (!isModalTier && !areRequiredParamsFulfilled)
        || isLoading
        || isGenerating
        || hasGenerated
      }
    >
      {label}
    </Button>
  )

  // While another action's run is in flight, this button is disabled; explain why
  // on hover. Otherwise fall back to any caller-provided tooltip (e.g. compatibility).
  const effectiveTooltipMessage = isBlockedByActiveGeneration ? GENERATION_IN_PROGRESS_TOOLTIP : tooltipMessage

  if (!effectiveTooltipMessage) {
    return button
  }

  return (
    <Tooltip content={effectiveTooltipMessage} id={`generate-button-tooltip-${triggerKey}`}>
      {button}
    </Tooltip>
  )
}

GenerateButton.propTypes = {
  generateLabel: string.isRequired,
  generatedLabel: string.isRequired,
  generatingLabel: string.isRequired,
  isEnhancementMode: bool,
  isDisabled: bool,
  tooltipMessage: string,
  withIcon: bool,
}

export default memo(GenerateButton)
